import '@features/virtual-tour';

import {
    defineElement,
    FC,
    onDidLoad,
    useElement,
    useEvent,
    useListen,
    useProp,
} from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { ExtendedApartmentBuilding, useStore } from '@bpd-library/utilities';
import { virtualView2dPointerEvents } from '@features/virtual-tour/constants';
import {
    findPointerInCurrentView,
    findView,
    pushView,
    setData,
    VirtualTourState,
    virtualTourStore,
} from '@features/virtual-tour/store';
import {
    VirtualTourImageView,
    VirtualTourPointer,
    VirtualView,
    VRTourPointerFilter,
} from '@features/virtual-tour/types';
import { VirtualTourElement } from '@features/virtual-tour/virtual-tour';

import { apartmentsViewEvents } from './utilities/constants';
import {
    getApartmentBuilding,
    getViewIdByConstructionNumberId,
    transformApartmentBuildingViews,
} from './utilities/transform-data';

interface ApartmentsViewElement {
    constructionNumberId: string | null;
    expanded: boolean;
}

export const ApartmentsView: FC<ApartmentsViewElement> = () => {
    const [expanded] = useProp<boolean | undefined>('expanded', false);
    const [vrTourId] = useProp<string>('vrTourId');
    const [onClose] = useProp<Function | null>('onClose', null);
    const [currConstructionNumberId, setConstructionNumberId, watchConstructionNumberId] = useProp<
        string | null
    >('constructionNumberId', null);

    const vrTourContainer = useElement<VirtualTourElement>('virtual-tour');

    const [, subscribe] = useStore<VirtualTourState>(virtualTourStore);

    let apartmentBuilding: ExtendedApartmentBuilding | undefined;

    const setViewData = async (options: {
        constructionNumberId?: string | null;
        linkToViewId?: string | null;
        triggerEvent?: boolean;
    }) => {
        const { constructionNumberId, linkToViewId } = options;
        const constructionNumberViewId =
            constructionNumberId && (await getViewIdByConstructionNumberId(constructionNumberId));

        const viewId = linkToViewId || constructionNumberViewId;
        if (!viewId) return;

        apartmentBuilding = await getApartmentBuilding({ viewId });
        options.triggerEvent && handleApartmentBuildingChange();

        const { views, minimap } = await transformApartmentBuildingViews(apartmentBuilding);
        views && setData(vrTourId, { views, firstViewId: viewId, minimap });
    };

    useListen(document, apartmentsViewEvents.ALIAS_CHANGED, (e) => {
        const { constructionNumberId } = e.detail;
        setViewData({ constructionNumberId });
    });

    useListen(document, virtualView2dPointerEvents.LABEL_FOCUS, ({ detail }) => {
        setConstructionNumberId(detail.active ? detail.id : null);
    });

    watchConstructionNumberId((id) => {
        if (!id) {
            vrTourContainer.current?.focusPointer(() => false);
            return;
        }

        const pointerFilter: VRTourPointerFilter = (p) => p.constructionNumber?.id === id;

        const pointer = findPointerInCurrentView(vrTourId, pointerFilter);

        if (!pointer) {
            const view = findView(vrTourId, (v: VirtualView) => {
                if (v.type !== '2d') return false;
                else v = v as VirtualTourImageView;

                if (v.pointers.find(pointerFilter)) return true;
                return false;
            });

            if (!view) return;

            pushView(vrTourId, view.id);
        }
        setTimeout(() => {
            vrTourContainer.current?.focusPointer(pointerFilter);
        }, 50);
    });

    onDidLoad(() => {
        setViewData({ constructionNumberId: currConstructionNumberId });
    });

    subscribe(
        async ({ [vrTourId]: vrState }) => {
            const { apartmentBuildingId, id: viewId } = vrState.currentView!;
            if (apartmentBuildingId && apartmentBuildingId !== apartmentBuilding?.id) {
                apartmentBuilding = await getApartmentBuilding({ viewId });
                handleApartmentBuildingChange();

                const { minimap } = await transformApartmentBuildingViews(apartmentBuilding);
                minimap && vrTourContainer.current?.setMinimap(minimap, vrState.currentView!);
            }
        },
        [vrTourId],
    );

    const apartmentBuildingChangeEvent = useEvent({ eventName: apartmentsViewEvents.UPDATE_ALIAS });
    function handleApartmentBuildingChange() {
        apartmentBuildingChangeEvent.emit({
            apartmentBuildingId: apartmentBuilding?.id,
        });
    }

    return (
        <div class={`c-apartments-view apartments-view--${expanded ? 'expanded' : 'contained'}`}>
            <virtual-tour
                id={vrTourId}
                onClose={onClose}
                onMissingData={(viewId: string) => {
                    setViewData({ linkToViewId: viewId, triggerEvent: true });
                }}
                {...(currConstructionNumberId && {
                    pointerHighlighted: (p: VirtualTourPointer) =>
                        p.constructionNumber?.id === currConstructionNumberId,
                })}
            />
        </div>
    );
};

ApartmentsView.props = {
    expanded: {
        type: Boolean,
    },
    constructionNumberId: {
        type: String,
    },
    vrTourId: {
        type: String,
        required: true,
    },
    onClose: {
        type: Function,
    },
};

defineElement('apartments-view', ApartmentsView);
